import { useState, useEffect } from 'react';
import {
  User,
  Save,
  Key,
  Award,
  Building,
  CheckCircle2,
  Lock,
  ExternalLink,
} from 'lucide-react';
import { useAuthStore } from '../stores/auth';
import { toast } from 'sonner';

export function AccountPage() {
  const { user } = useAuthStore();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    organization: '',
    department: '',
    apiCertifications: [] as string[],
  });
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (user) {
      setFormData({
        name: user.name || 'Marcus Vance, PE',
        email: user.email || 'm.vance@petrochem-refining.com',
        organization: (user as any).organization || 'PetroChem Refining Operations BV',
        department: (user as any).department || 'Mechanical Integrity & Static Equipment',
        apiCertifications: (user as any).apiCertifications?.length
          ? (user as any).apiCertifications
          : ['API 570', 'API 510', 'API 580', 'NACE/AMPP'],
      });
    } else {
      setFormData({
        name: 'Marcus Vance, PE',
        email: 'm.vance@petrochem-refining.com',
        organization: 'PetroChem Refining Operations BV',
        department: 'Mechanical Integrity & Static Equipment',
        apiCertifications: ['API 570', 'API 510', 'API 580', 'NACE/AMPP'],
      });
    }
  }, [user]);

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSaving(true);
    try {
      await new Promise((resolve) => setTimeout(resolve, 600));
      toast.success('Engineer profile & credentials saved successfully');
    } catch {
      toast.error('Failed to update account preferences');
    } finally {
      setIsSaving(false);
    }
  };

  const availableCerts = [
    { code: 'API 570', title: 'Piping Inspector' },
    { code: 'API 510', title: 'Pressure Vessel Inspector' },
    { code: 'API 653', title: 'Aboveground Storage Tank' },
    { code: 'API 580', title: 'Risk-Based Inspection' },
    { code: 'API 581', title: 'Quantitative RBI Analyst' },
    { code: 'NACE/AMPP', title: 'Senior Corrosion Specialist' },
    { code: 'ASNT Level III', title: 'Electromagnetic & Radiography' },
  ];

  return (
    <div className="space-y-8 pb-12">
      {/* Top Banner Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-6 border-b border-slate-200">
        <div>
          <div className="flex items-center gap-2 text-xs font-mono text-blue-700 bg-blue-50 px-2.5 py-1 rounded w-fit mb-2 border border-blue-200">
            <Award className="h-3.5 w-3.5" />
            OPERATOR IDENTITY & REGULATORY LICENSING
          </div>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 flex items-center gap-3">
            <User className="h-7 w-7 text-blue-600" />
            Inspector Profile & Credentials
          </h1>
          <p className="text-slate-600 text-sm mt-1">
            Maintain operator certifications, facility domain associations, and enterprise CMMS dispatch signatures.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Left 2 Columns: Profile Form */}
        <div className="lg:col-span-2 space-y-6">
          <form onSubmit={handleSave} className="tech-panel p-6 bg-white border border-slate-200 rounded-xl shadow-sm space-y-6">
            <div className="flex items-center justify-between pb-4 border-b border-slate-200">
              <h2 className="text-base font-bold text-slate-900 flex items-center gap-2">
                <Building className="h-5 w-5 text-slate-600" />
                Plant Integrity Credentials
              </h2>
              <span className="font-mono text-xs px-2.5 py-1 rounded bg-emerald-50 text-emerald-700 border border-emerald-300 font-semibold flex items-center gap-1.5">
                <CheckCircle2 className="h-3.5 w-3.5" />
                VERIFIED OPERATOR
              </span>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div>
                <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-2">
                  Full Name & Professional Title
                </label>
                <input
                  type="text"
                  className="w-full px-3.5 py-2.5 rounded-lg border border-slate-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-2">
                  Enterprise Work Email
                </label>
                <input
                  type="email"
                  className="w-full px-3.5 py-2.5 rounded-lg border border-slate-200 bg-slate-50 text-slate-500 font-mono text-xs cursor-not-allowed"
                  value={formData.email}
                  disabled
                />
                <p className="text-[11px] text-slate-500 mt-1 flex items-center gap-1">
                  <Lock className="h-3 w-3" /> Managed centrally via Logto OIDC Single Sign-On
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div>
                <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-2">
                  Operating Company / Client
                </label>
                <input
                  type="text"
                  className="w-full px-3.5 py-2.5 rounded-lg border border-slate-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                  value={formData.organization}
                  onChange={(e) => setFormData({ ...formData, organization: e.target.value })}
                  placeholder="e.g. TotalEnergies, Shell, BASF"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-2">
                  Facility Unit / Department
                </label>
                <input
                  type="text"
                  className="w-full px-3.5 py-2.5 rounded-lg border border-slate-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                  value={formData.department}
                  onChange={(e) => setFormData({ ...formData, department: e.target.value })}
                  placeholder="e.g. Asset Integrity & Piping Systems"
                />
              </div>
            </div>

            {/* API Certifications Picker */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wider">
                  Active Professional Certifications (API / NACE / ASNT)
                </label>
                <span className="text-xs text-slate-500">
                  {formData.apiCertifications.length} active credentials
                </span>
              </div>
              <p className="text-xs text-slate-500 mb-3">
                Select your current active licenses. These stamps are applied to generated API 579 FFS calculation reports and SAP work order authorizations.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                {availableCerts.map((cert) => {
                  const isChecked = formData.apiCertifications.includes(cert.code);
                  return (
                    <button
                      type="button"
                      key={cert.code}
                      onClick={() => {
                        const updated = isChecked
                          ? formData.apiCertifications.filter((c) => c !== cert.code)
                          : [...formData.apiCertifications, cert.code];
                        setFormData({ ...formData, apiCertifications: updated });
                      }}
                      className={`flex items-center justify-between p-3 rounded-lg border text-left transition-all cursor-pointer ${
                        isChecked
                          ? 'bg-blue-50 border-blue-300 text-blue-900 shadow-xs'
                          : 'bg-white border-slate-200 text-slate-700 hover:bg-slate-50'
                      }`}
                    >
                      <div>
                        <div className="font-mono font-bold text-xs">{cert.code}</div>
                        <div className="text-[11px] text-slate-500">{cert.title}</div>
                      </div>
                      <div
                        className={`h-5 w-5 rounded flex items-center justify-center text-xs font-bold ${
                          isChecked
                            ? 'bg-blue-600 text-white'
                            : 'border border-slate-300 bg-white text-transparent'
                        }`}
                      >
                        ✓
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="pt-4 border-t border-slate-200 flex items-center justify-end">
              <button
                type="submit"
                disabled={isSaving}
                className="btn-primary flex items-center gap-2 cursor-pointer"
              >
                <Save className="h-4 w-4" />
                {isSaving ? 'Saving Updates...' : 'Save Profile Changes'}
              </button>
            </div>
          </form>

          {/* Security & Access Panel */}
          <div className="tech-panel p-6 bg-white border border-slate-200 rounded-xl shadow-sm space-y-4">
            <h2 className="text-base font-bold text-slate-900 flex items-center gap-2">
              <Key className="h-5 w-5 text-slate-600" />
              Authentication & Enterprise MFA
            </h2>
            <div className="p-4 bg-slate-50 rounded-lg border border-slate-200 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div className="space-y-1">
                <div className="font-semibold text-sm text-slate-900 flex items-center gap-2">
                  <span className="h-2 w-2 rounded-full bg-emerald-500"></span>
                  Logto Zero-Trust Identity Provider
                </div>
                <div className="text-xs text-slate-600 leading-relaxed max-w-lg">
                  Password rotation, hardware security keys (FIDO2 / WebAuthn), and enterprise SSO are federated with your corporate Azure AD / Okta directory.
                </div>
              </div>
              <a
                href="https://auth-dev.sensifai.com/console"
                target="_blank"
                rel="noopener noreferrer"
                className="btn-secondary btn-sm inline-flex items-center gap-2 shrink-0 self-start sm:self-center"
              >
                Manage Auth Console
                <ExternalLink className="h-3.5 w-3.5" />
              </a>
            </div>
          </div>
        </div>

        {/* Right Sidebar */}
        <div className="space-y-6">
          {/* Operator Badge Card */}
          <div className="tech-panel p-6 bg-white border border-slate-200 rounded-xl shadow-sm text-center">
            <div className="h-20 w-20 rounded-2xl bg-gradient-to-br from-blue-600 to-indigo-700 text-white flex items-center justify-center mx-auto mb-4 shadow-md font-mono text-2xl font-bold border-2 border-white ring-2 ring-blue-200">
              {formData.name.split(' ').map((n) => n[0]).slice(0, 2).join('') || 'MV'}
            </div>
            <h3 className="font-bold text-base text-slate-900">{formData.name}</h3>
            <p className="text-xs font-mono text-slate-500 mt-0.5">{formData.email}</p>

            <div className="mt-4 pt-4 border-t border-slate-100 flex flex-wrap justify-center gap-1.5">
              <span className="font-mono text-[10px] px-2 py-0.5 rounded bg-blue-100 text-blue-800 font-semibold">
                SENIOR INTEGRITY ENGINEER
              </span>
              <span className="font-mono text-[10px] px-2 py-0.5 rounded bg-emerald-100 text-emerald-800 font-semibold">
                ROLE: ADMIN
              </span>
            </div>
          </div>

          {/* Compliance & Authorization Dossier */}
          <div className="tech-panel p-6 bg-white border border-slate-200 rounded-xl shadow-sm space-y-4">
            <h3 className="font-bold text-sm text-slate-900 flex items-center gap-2">
              <Award className="h-4 w-4 text-blue-600" />
              Authorization & Scope
            </h3>
            <div className="space-y-3 font-mono text-xs">
              <div className="flex justify-between py-1.5 border-b border-slate-100">
                <span className="text-slate-500">Security Clearance</span>
                <span className="font-semibold text-slate-800">IEC 62443 SL-3</span>
              </div>
              <div className="flex justify-between py-1.5 border-b border-slate-100">
                <span className="text-slate-500">Facility Code</span>
                <span className="font-semibold text-slate-800">ROTTERDAM-REFINERY-A</span>
              </div>
              <div className="flex justify-between py-1.5 border-b border-slate-100">
                <span className="text-slate-500">FFS Sign-Off Limit</span>
                <span className="font-semibold text-emerald-600">Level 1 & Level 2</span>
              </div>
              <div className="flex justify-between py-1.5">
                <span className="text-slate-500">CMMS Dispatch Rights</span>
                <span className="font-semibold text-blue-600">SAP PM + IBM Maximo</span>
              </div>
            </div>
          </div>

          {/* Quick Help Card */}
          <div className="p-5 rounded-xl bg-slate-900 text-white border border-slate-800 space-y-3">
            <div className="font-semibold text-xs text-blue-300 font-mono flex items-center gap-1.5">
              <CheckCircle2 className="h-3.5 w-3.5 text-blue-400" />
              AUDIT RECORD COMPLIANCE
            </div>
            <p className="text-xs text-slate-300 leading-relaxed">
              Every AI defect confirmation, temperature override, and fitness-for-service sign-off is digitally timestamped with your cryptographic signature.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
